import React, { useState, useEffect, Fragment } from "react";

import { observer } from "mobx-react-lite";
import { makeStyles } from "@mui/styles";
import { useStores } from "../../../core/stores/index";
import "./ClaimList.css";
import { MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { Spinner } from "components/common/Spinner";
import { ClaimStore } from "core/stores/ClaimStore";
import { ClaimType } from "common/enums/ClaimType";
import { ClaimStatus } from "common/enums/ClaimStatus";
import { IClaim } from "common/interfaces/IClaim";
import { useNavigate } from "react-router-dom";
import { DataGrid, GridColDef, GridFilterModel } from "@mui/x-data-grid";
import { DateHelper } from "common/helpers/DateHelper";
import { TiTick } from "react-icons/ti";
import { UiHelper } from "../../../common/helpers/UiHelper";
import * as XLSX from 'xlsx-js-style';

interface ClaimListProp {
    children: JSX.Element;
}

const sanitizeSheetName = (name?: string) => {
    const safe = (name || 'Claims')
        .replace(/[\[\]\:\*\?\/\\]/g, '') // illegal Excel chars
        .trim()
        .slice(0, 31);                    // Excel max length
    return safe.length ? safe : 'Claims';
};

// Helper: convert JS Date -> Excel serial number
function toExcelSerial(d: Date): number {
  // Excel serial: days since 1899-12-30
  return d.getTime() / (1000 * 60 * 60 * 24) + 25569;
}

// Optional: if you prefer real Excel dates instead of text
function parseDateSafe(value?: string | number | Date): Date | null {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export const SearchTypeName: Record<number, string> = {
    1: 'All claims',
    2: 'All my claims',
    3: 'My Pending Claims'
};

export const ClaimTypeLabel: Record<number, string> = {
    1: 'Price Claim',
    2: 'Inventory Claim',
    3: 'Uplift Request',
    4: 'RMA',
    5: "Ad Hoc"
};

export const StatusTypeLabel: Record<number, string> = {
    1: 'Draft',
    2: 'Submitted For Investigation',
    3: 'Under Level 2 Investigation',
    4: 'Invalid',
    5: "PartValid",
    6:"Pending Approval",
    7: "Approved",
    8: "Rejected",
    9: "Cancelled",
    10: "Closed"
};

export function formatDate(
    value?: string | number | Date,
    locale: string = 'en-GB'
    ): string {
        // Missing value → empty string
        if (value == null) return '';

        // Normalize to Date
        const d = value instanceof Date ? value : new Date(value);

        // Invalid date (e.g., bad string) → empty string
        if (isNaN(d.getTime())) return '';

        // dd/MM/yyyy hh:mm (24h)
        return new Intl.DateTimeFormat(locale, {
            day: '2-digit',
            month: '2-digit', // use 'short' for Jan/Feb: e.g., 17 Dec 2025
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).format(d);
    }

export const ClaimList = observer((props: ClaimListProp) => {
    const navigate = useNavigate();
    const { claimStore } = useStores();

    const [showLoader, setShowLoader] = useState<boolean | undefined>(undefined);

    const [ignoreFilterChange, setIgnoreFilterChange] = useState<boolean>(true);
    const [ignoreSearchText, setIgnoreSearchText] = useState<boolean>(true);
   
    const [claims, setClaims] = useState<IClaim[]>([]);

    const [searchText, setSearchText] = useState<string>(() => {
        // Restore from sessionStorage if available
        return sessionStorage.getItem("searchText") || "";
    });

    

    const handleOnExport = async () => {
        setShowLoader(true);
        console.time('excel-export');

        try {
            console.log('[Export] clicked');

            const allClaims = await getNonPaginatedClaims();

            if (!Array.isArray(allClaims)) {
                throw new Error('Claims API returned a non-array response.');
            }
            if (allClaims.length === 0) {
                // Let user know rather than failing silently
                alert('No claims found to export.');
                return;
            }

            const rows = allClaims.map((c: any) => ({
                "Claim Ref": c.claimRef ?? '',
                "Raised by": c.raisedByUserName ?? '',
                "Date": c.creationDate ?? '', // we will format in the sheet
                "Account": c.customerAccount ?? '',
                "Invoice": c.invoiceNumber ?? '',
                "Claim value": typeof c.gbpValue === 'number'
                    ? c.gbpValue
                    : Number(c.gbpValue ?? 0),
                "Type": ClaimTypeLabel[c.claimType] ?? `Unknown (${c.claimType})`,
                "Status": StatusTypeLabel[c.claimStatus] ?? `Unknown (${c.claimStatus})`,
                "Actioner": c.actionerName ?? '',
            }));

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(rows);

            // === 1) Header styling ===
            const headers = Object.keys(rows[0] ?? {});
            const headerCount = headers.length;

            for (let c = 0; c < headerCount; c++) {
                const addr = XLSX.utils.encode_cell({ r: 0, c });
                const cell = ws[addr] || { v: headers[c], t: 's' };
                const isAmountCol = headers[c] === 'Claim value';

                ws[addr] = {
                    ...cell,
                    s: {
                        font: { name: 'Arial', sz: 12, bold: true, color: { rgb: '000000' } },
                        alignment: { horizontal: isAmountCol ? 'right' : 'left', vertical: 'middle' },
                        fill: { patternType: 'solid', fgColor: { rgb: 'F0F0F0' } },
                        border: {
                            top: { style: 'thin', color: { rgb: 'CCCCCC' } },
                            left: { style: 'thin', color: { rgb: 'CCCCCC' } },
                            bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
                            right: { style: 'thin', color: { rgb: 'CCCCCC' } },
                        },
                    },
                };
            }

            // === 2) Column widths ===
            ws['!cols'] = headers.map(key => {
                const maxLen = Math.max(
                    key.length,
                    ...rows.map(row => String(row[key as keyof typeof row] ?? '').length)
                );
                return { wch: Math.min(Math.max(maxLen + 2, 12), 50) };
            });

            // === 3) Data row styling + coercions ===
            const ref = ws['!ref'];
            if (ref) {
                const range = XLSX.utils.decode_range(ref);
                for (let r = 1; r <= range.e.r; r++) {
                    for (let c = 0; c <= range.e.c; c++) {
                        const addr = XLSX.utils.encode_cell({ r, c });
                        const cell = ws[addr];
                        if (!cell) continue;

                        const isDateCol = headers[c] === 'Date';
                        const isAmountCol = headers[c] === 'Claim value';

                        const baseStyle = {
                            font: { name: 'Arial', sz: 11 },
                            alignment: { horizontal: isAmountCol ? 'right' : 'left', vertical: 'middle' },
                            border: {
                                top: { style: 'thin', color: { rgb: 'EEEEEE' } },
                                left: { style: 'thin', color: { rgb: 'EEEEEE' } },
                                bottom: { style: 'thin', color: { rgb: 'EEEEEE' } },
                                right: { style: 'thin', color: { rgb: 'EEEEEE' } },
                            },
                        };
                        ws[addr] = { ...cell, s: { ...(cell.s || {}), ...baseStyle } };

                        if (isAmountCol) {
                            const num = typeof cell.v === 'number' ? cell.v : Number(cell.v);
                            if (!isNaN(num)) {
                                ws[addr].t = 'n';
                                ws[addr].v = num;
                                ws[addr].z = '0.00';
                            }
                        }

                        if (isDateCol) {
                            const d = parseDateSafe(cell.v as any);
                            if (d) {
                                ws[addr].t = 'n';
                                ws[addr].v = toExcelSerial(d);
                                ws[addr].z = 'dd/mm/yyyy hh:mm';
                            }
                        }
                    }
                }
            }

            const sheetName = sanitizeSheetName(SearchTypeName[claimFilterSelection!] ?? 'Claims');
            XLSX.utils.book_append_sheet(wb, ws, sheetName);

            const fileName = 'MyExcel.xlsx';

            // --- Primary path: built-in writeFile (anchor download) ---
            try {
                console.log('[Export] using XLSX.writeFile');
                XLSX.writeFile(wb, fileName);
            } catch (e) {
                console.warn('[Export] writeFile failed, falling back to Blob method', e);

                // --- Fallback 1: manual Blob download ---
                try {
                    const wbArray = XLSX.write(wb, { bookType: 'xlsx', type: 'array', compression: true });
                    const blob = new Blob([wbArray], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    });
                    const url = URL.createObjectURL(blob);

                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                } catch (blobErr) {
                    console.warn('[Export] Blob download failed, trying window.open', blobErr);

                    // --- Fallback 2: open in new tab (helps with some iframe/CSP setups) ---
                    try {
                        const wbArray = XLSX.write(wb, { bookType: 'xlsx', type: 'array', compression: true });
                        const blob = new Blob([wbArray], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        });
                        const url = URL.createObjectURL(blob);
                        window.open(url, '_blank'); // may still be blocked depending on sandbox
                    } catch (openErr) {
                        console.error('[Export] All client-side strategies failed', openErr);
                        alert('Download blocked by browser/portal. Please contact support or use the server-side export.');
                    }
                }
            }

        } catch (err: any) {
            console.error('[Export] failed:', err);
            alert(`Export failed: ${err?.message || err}`);
        } finally {
            setShowLoader(false);
            console.timeEnd('excel-export');
        }
    };

    

    const [customer, setCustomer] = useState<string>(() => sessionStorage.getItem("customer") || "");

    const [status, setStatus] = useState<number | undefined>(() => {
        const val = sessionStorage.getItem("status");
        return val ? Number(val) : undefined;
    });

    const [claimFilterSelection, setClaimFilterSelection] = useState<number>(() => {
        const val = sessionStorage.getItem("claimFilterSelection");
        return val ? Number(val) : 1; // Default to 1
    });

    // Save filters whenever they change
    useEffect(() => {
        sessionStorage.setItem("searchText", searchText);
        sessionStorage.setItem("customer", customer);
        sessionStorage.setItem("status", status?.toString() || "");
        sessionStorage.setItem("claimFilterSelection", claimFilterSelection.toString());
    }, [searchText, customer, status, claimFilterSelection]);

    const [pageSize,setPageSize] = useState<number>(30);

    const handleClaimSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setClaimFilterSelection(Number(e.target.value));
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    };

    const onSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
        setIgnoreFilterChange(true);
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    };

    const [rowCount, setRowCount] = useState(0);

    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: pageSize,
    });

    const getNonPaginatedClaims = async (): Promise<IClaim[]> => {
        const response = await claimStore.getPaginatedClaims(
            0,                    
            0,                    
            searchText,
            customer,
            status as number,
            claimFilterSelection
        );
        
        return response.claims;
    };

    const getPaginatedClaims = () => {
        setShowLoader(true);
        claimStore
            .getPaginatedClaims(paginationModel.page, paginationModel.pageSize, searchText, customer, status as number, claimFilterSelection)
            .then((response) => {
                setClaims(response.claims);
                setShowLoader(false);
                setRowCount(response.totalCount);
            });
    };

    // Initial load
    useEffect(() => {
        setIgnoreFilterChange(true);
        setIgnoreSearchText(true);
        getPaginatedClaims();
        
    }, []);

    // Debounced search effect
    useEffect(() => {
        const handler = setTimeout(() => {
            if (ignoreSearchText == false && (searchText.length >= 3 || searchText.length === 0)) {
                getPaginatedClaims();
            }

            if (ignoreSearchText == true)
            {
                setIgnoreSearchText(false);
            }

        }, 600); 

        return () => {
            clearTimeout(handler); // cleanup previous timeout
        };
    }, [searchText]);

    // Pagination or filter changes
    useEffect(() => {
        if(ignoreFilterChange == false)
        {
            getPaginatedClaims();
        }
        else
        {
            setIgnoreFilterChange(false);
        }
    }, [paginationModel, customer, status, claimFilterSelection]);

    const claimClickHandler = (claimId: number) => {
        claimStore.clearSelectedClaimAndType();
        navigate(`/claimdetails/${claimId}`);
    };    

    const getColumns = (): GridColDef[] => {
        const columns = [
            {
                field: "claimRef",
                headerName: "Claim Ref",
                width: 150,
                renderCell: (params: any) => <div>{params.row.claimRef}</div>,
            },
            {
                field: "raisedBy",
                headerName: "Raised By",
                width: 150,
                renderCell: (params: any) => (
                    <a className="hyperink" href="#" onClick={() => claimClickHandler(params.row.claimId)}>
                        {params.row.raisedByUserName}
                    </a>
                ),
            },
            {
                field: "creationDate",
                width: 150,
                headerName: "Date",
                renderCell: (params: any) => <div>{DateHelper.getShortTextDate(params.row.creationDate, true, false)}</div>,
            },
            {
                field: "account",
                width: 150,
                headerName: "Account",
                renderCell: (params: any) => <div>{params.row.customerAccount}</div>,                
                disableColumnFilter: true ,
                
                renderHeader: (params: any) => {
                    const uniqueValues = Array.from(new Set(claims.map((row) => row.customerAccount)));

                    return (
                        <div>
                            <div>
                             <select value={customer} className="form-control w-auto form-select-sm"  onChange={onCustomerSelection}>
                                <option value="">All</option>
                                {uniqueValues.map((value) => (
                                    <option key={value} value={value}>
                                        {value}
                                    </option>
                                ))}
                            </select>
                            </div>
                            <div className="header-text">{params.colDef.headerName}</div>
                           
                        </div>
                    );
                },
            },
            {
                field: "invoiceNumber",
                width: 150,
                headerName: "Invoice",
                renderCell: (params: any) => <div>{params.row.invoiceNumber}</div>,
            },
            {
                field: "gbpValue",
                width: 150,
                headerName: "Claim Value",
                renderCell: (params: any) => <div>{params.row.gbpValue}</div>,
            },
            {
                field: "type",
                width: 150,
                headerName: "Type",
                renderCell: (params: any) => <div>{ClaimType[params.row.claimType]}</div>,
            },
            {
                field: "status",
                width: 220,
                headerName: "Status",
                renderCell: (params: any) => <div>{ClaimStatus[params.row.claimStatus]}</div>,
                renderHeader: (params: any) => {                   

                    return (
                        <div>
                            <div>
                             <select value={status}  className="form-control w-auto form-select-sm"  onChange={onStatusSelection}>
                                <option  value="">All</option>                                
                                    <option key={1} value={1}>
                                        {"Draft"}
                                    </option>
                                    <option key={2} value={2}>
                                        {"Submitted"}
                                    </option>
                                    <option key={3} value={3}>
                                        {"L2 Investigation"}
                                    </option>
                                    <option key={4} value={4}>
                                        {"Invalid"}
                                    </option>
                                    <option key={5} value={5}>
                                        {"Part Valid"}
                                    </option>
                                    <option key={6} value={6}>
                                        {"Approval"}
                                    </option>
                                    <option key={7} value={7}>
                                        {"Approved"}
                                    </option>
                                    <option key={8} value={8}>
                                        {"Rejected"}
                                    </option>
                                    <option key={9} value={9}>
                                        {"Cancelled"}
                                    </option>
                                    <option key={10} value={10}>
                                        {"Closed"}
                                    </option>
                            </select>
                            </div>
                            <div className="header-text">{params.colDef.headerName}</div>                           
                        </div>
                    );
                },
                filterable: true,
            },
            {
                field: "actioner",
                width: 150,
                headerName: "Actioner",
                renderCell: (params: any) => <div>{params.row.actionerName}</div>,
            },
            {
                field: "postedToIntegration",
                width: 150,
                headerName: "D365",
                renderCell: (params: any) => <div>{params.row.postedToIntegration?(<><TiTick className="tick-icon-size"/></>):(<></>)}</div>,
            },
        ];

        return columns;
    };
    
    const onCustomerSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setCustomer(e.target.value);
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    };

    const onStatusSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setStatus(Number(e.target.value));
        setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    };

    const showCustomLoader = () => {
        return <Spinner/>;
    }

    return (
        <div className="container-fluid p-1">
            <div className="m-4 card">
                <div className="card-header">
                    <div className="row">
                        <div className="col-7 align-content-center">
                            <h1>Claims</h1>
                        </div>
                        <div className="col-2 p-1">
                            <div className="p-2">
                                <select
                                    value={claimFilterSelection}
                                    onChange={(e) => handleClaimSelectionChange(e)}
                                    className="form-control w-auto mr-sm-2"
                                    name="Filter"
                                >
                                    <option value={1}>All claims</option>
                                    <option value={2}>All my claims</option>
                                    <option value={3}>My Pending Claims</option>
                                </select>
                            </div>
                        </div>
                        <div className="col-2 align-content-center">
                            <input
                                type="search"
                                onChange={onSearchTextChange}
                                value={searchText}
                                className="form-control mr-sm-2"
                                id="searchText"
                                placeholder="search"
                            />
                            
                        </div>
                        <div className="col-1 align-content-center">
                            <button className="btn btn-primary me-1 btn-sm" onClick={() => handleOnExport()} type="button">
                                Export
                            </button>
                        </div>
                    </div>
                </div>
                <div className="col-6 my-2 my-lg-0 align-content-center"></div>
                <div className="row card-body w-100 h-100">
                    <div className="col table-responsive p-2 grid-parent-container">                      
                            <DataGrid
                                sx={{
                                    ".MuiDataGrid-columnHeaderTitle": {
                                        fontWeight: "600 !important",
                                    },
                                    ".MuiDataGrid-cell": {
                                        fontWeight: "400 !important",
                                    },
                                }}
                                columns={getColumns()}
                                rows={claims}
                                paginationMode="server"
                                rowCount={rowCount}
                                pageSizeOptions={[pageSize]}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                loading={showLoader}
                                slots={{
                                    loadingOverlay: showCustomLoader,
                                  }}
                                getRowId={(row) => row.claimId}
                                filterMode="server"
                                columnHeaderHeight={80}
                                disableColumnFilter={true}
                                disableColumnSorting={true}
                                disableColumnMenu={true}
                            />                    
                    </div>
                </div>
            </div>
        </div>
    );
});
